import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import PageHeader from '../../../shared/PageHeader';
import ActionsMenu from '../../../shared/ActionsMenu';
import { askConfirm } from '../../../shared/AppDialogs';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { formatDate, formatDateTime } from '../../../shared/tenantClock';
import { formatMoney } from '../../../shared/numberFormat';
import { ReceiptQuickbooksTag, receiptVoidedLabel, voidReceiptQuestion, markInQuickbooksQuestion } from '../../../shared/receiptShared';
import { VoidPill } from '../Invoices/InvoicesTable';
import './ReceiptPage.css';

// One Receipt (issue #442). It's recorded on its Quote or Sales Order and
// never edited; here it's voided, or retried or marked as already in
// QuickBooks when it hasn't reached QuickBooks, the same as on the
// document's Payments card. A Receipt taken on a Quote that's since been
// converted links to both.
const fieldLabelStyle = { display: 'block', fontSize: 12, color: '#555', marginBottom: 4 };
const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };

function Field({ label, children }) {
  return (
    <div>
      <label style={fieldLabelStyle}>{label}</label>
      <div style={{ fontSize: 14 }}>{children || '—'}</div>
    </div>
  );
}

function ReceiptPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const { canUpdate } = useScreenAccess();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`/api/receipts/${id}`)
      .then((res) => setReceipt(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load the Receipt'));
  }, [id]);

  // Void, Retry, or Mark as already in QuickBooks: the response is the
  // Receipt as it now stands.
  const step = async (path, failure) => {
    setBusy(true);
    try {
      const res = await axios.post(`/api/receipts/${id}/${path}`);
      setReceipt(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, failure);
    }
    setBusy(false);
  };
  const handleVoid = async () => {
    if (!(await askConfirm(voidReceiptQuestion(receipt), { confirmLabel: 'Void', destructive: true }))) return;
    step('void', 'Failed to void Receipt');
  };
  const handleMark = async () => {
    if (!(await askConfirm(markInQuickbooksQuestion(receipt), { confirmLabel: 'Mark as in QuickBooks' }))) return;
    step('mark-in-quickbooks', 'Failed to mark Receipt');
  };

  if (error) return <div style={{ margin: 32 }}>{error}</div>;
  if (!receipt) return <div style={{ margin: 32 }}>Loading...</div>;

  const isVoid = receipt.status === 'void';
  const failed = receipt.quickbooks?.state === 'failed';
  // A Receipt on an order dated before the Invoice Cutover is read-only history.
  const actionsMenuItems = isVoid || receipt.read_only ? [] : [
    { key: 'void', label: 'Void Receipt', needs: 'update', onSelect: handleVoid, disabled: busy },
  ];
  const documentLink = (doc, path) => doc && (
    <button type="button" className="link-button" onClick={() => navigate(`/${path}/${doc.id}`)}>{doc.display_number}</button>
  );

  return (
    <div className="receipt-page" style={{ width: '100%', maxWidth: 960, margin: '32px auto 48px', padding: '0 24px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <PageHeader
            crumbs={[{ label: 'Receipts', onClick: () => navigate('/receivables-receipts') }, { label: receipt.display_number }]}
            title={(
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <span style={{ textDecoration: isVoid ? 'line-through' : 'none' }}>Receipt {receipt.display_number}</span>
                {isVoid && <VoidPill />}
              </span>
            )}
            subtitle={receipt.customer?.name}
          />
        </div>
        {actionsMenuItems.length > 0 && <ActionsMenu items={actionsMenuItems} placement="down" style={{ marginTop: 36 }} />}
      </div>

      {isVoid && (
        <div style={{ fontSize: 13, color: '#5a6b85', background: '#eef1f5', border: '1px solid #d5dbe3', borderRadius: 6, padding: '8px 12px', marginBottom: 16 }}>
          {receiptVoidedLabel(receipt)}
        </div>
      )}

      {failed && (
        <div style={{ fontSize: 13, color: '#b91c1c', background: '#fdecec', border: '1px solid #f5c2c2', borderRadius: 6, padding: '8px 12px', marginBottom: 16 }}>
          <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span><strong>Not in QuickBooks:</strong> {receipt.quickbooks.message}</span>
            {canUpdate && (
              <button type="button" onClick={() => step('quickbooks-retry', 'Retry failed')} disabled={busy} title="Sends this Receipt to QuickBooks again" style={LINK_BUTTON}>
                {busy ? 'Sending...' : 'Retry'}
              </button>
            )}
            {canUpdate && !isVoid && (
              <button type="button" onClick={handleMark} disabled={busy} title="Marks this Receipt as already in QuickBooks, so it's never copied" style={LINK_BUTTON}>
                Mark as already in QuickBooks
              </button>
            )}
          </span>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
          <Field label="Date Paid">{formatDate(receipt.payment_date)}</Field>
          <Field label="Amount">
            <span style={{ fontWeight: 600, textDecoration: isVoid ? 'line-through' : 'none' }}>{formatMoney(receipt.amount)}</span>
          </Field>
          <Field label="Payment Reference">{receipt.reference}</Field>
          <Field label="Paid into">{receipt.paid_into_label}</Field>
          <Field label="Customer">
            {receipt.customer && (
              <button type="button" className="link-button" onClick={() => navigate(`/commercial-entities/${receipt.customer.id}`)}>{receipt.customer.name}</button>
            )}
          </Field>
          {receipt.quote && <Field label="Quote">{documentLink(receipt.quote, 'quotes')}</Field>}
          {receipt.sales_order && <Field label="Sales Order">{documentLink(receipt.sales_order, 'sales-orders')}</Field>}
          <Field label="Recorded">
            {[formatDateTime(receipt.recorded_at), receipt.recorded_by_name ? `by ${receipt.recorded_by_name}` : null].filter(Boolean).join(' ')}
          </Field>
          <Field label="QuickBooks">
            {receipt.quickbooks ? <ReceiptQuickbooksTag receipt={receipt} style={{ fontSize: 13 }} /> : 'Not copied'}
          </Field>
        </div>
      </div>
    </div>
  );
}

export default ReceiptPage;
